/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BACKGROUND SYNC SCHEDULER
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * Manages automatic background synchronization between Dashboard and Sequencers
 * 
 * FEATURES:
 * - Automatic sync every 5 minutes
 * - Daily discrepancy checks
 * - Configurable schedule
 * - Start/stop controls
 * - Error handling and retry logic
 * 
 * USAGE:
 *   BackgroundSyncScheduler.start()
 *   BackgroundSyncScheduler.stop()
 */

import { mailboxTwoWaySyncService } from './mailbox-two-way-sync-service'
import { slackAlertService } from './slack-alert-service'
import type { SequencerType } from './two-way-sync-types'

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface SchedulerConfig {
  syncIntervalMinutes: number
  discrepancyCheckHour: number // Hour of day (0-23) to run daily check
  maxRetries: number
  retryDelayMinutes: number
  enabled: boolean
}

interface SchedulerStatus {
  isRunning: boolean
  lastSyncAt: Date | null
  nextSyncAt: Date | null
  lastDiscrepancyCheckAt: Date | null
  nextDiscrepancyCheckAt: Date | null
  consecutiveFailures: number
}

// ═══════════════════════════════════════════════════════════════════════════
// BACKGROUND SYNC SCHEDULER
// ═══════════════════════════════════════════════════════════════════════════

class BackgroundSyncSchedulerClass {
  private syncIntervalId: NodeJS.Timeout | null = null
  private discrepancyIntervalId: NodeJS.Timeout | null = null
  
  private config: SchedulerConfig = {
    syncIntervalMinutes: 30, // Reduced from 5 min to avoid excessive SmartLead API calls and console noise
    discrepancyCheckHour: 2, // 2 AM daily
    maxRetries: 3,
    retryDelayMinutes: 2,
    enabled: true,
  }

  private status: SchedulerStatus = {
    isRunning: false,
    lastSyncAt: null,
    nextSyncAt: null,
    lastDiscrepancyCheckAt: null,
    nextDiscrepancyCheckAt: null,
    consecutiveFailures: 0,
  }

  private sequencerType: SequencerType = 'smartlead'
  private listeners: Array<(status: SchedulerStatus) => void> = []

  // ═════════════════════════════════════════════════════════════════════════
  // START SCHEDULER
  // ═════════════════════════════════════════════════════════════════════════

  public start(sequencerType: SequencerType = 'smartlead'): void {
    // ── KILL SWITCH (2026-06-11, 86ca7n8d4 — Sabeeh's call) ────────────
    // The browser-based background sync is RETIRED. It used to auto-start
    // for every session (incl. client users) and batch-write mailbox
    // updates to SmartLead across all clients while flooding the console
    // and freezing tabs. Canonical SmartLead state is owned by the
    // daily-mailbox-status-sync edge function (6 AM UTC cron); admins can
    // still trigger an explicit one-off sync via the Sync button on the
    // Domains & Mailboxes page (useMailboxTwoWaySync.performSync — that
    // path does NOT go through this scheduler). If a periodic browser
    // sync is ever genuinely needed again, remove this guard consciously.
    console.warn(
      '[mailbox-sync] BackgroundSyncScheduler.start() is disabled (86ca7n8d4). ' +
      'The daily-mailbox-status-sync edge function owns SmartLead sync; ' +
      'use the manual Sync button on Domains & Mailboxes for one-off runs.',
    )
    return
    // Unreachable — retained for reference should the scheduler be revived.
    /* eslint-disable no-unreachable */

    if (this.status.isRunning) {
      return
    }

    this.sequencerType = sequencerType
    this.status.isRunning = true

    // Start sync interval
    this.startSyncInterval()

    // Start discrepancy check interval
    this.startDiscrepancyCheckInterval()

    // Run initial sync
    this.performSync()

    this.notifyListeners()
  }

  // ═════════════════════════════════════════════════════════════════════════
  // STOP SCHEDULER
  // ═════════════════════════════════════════════════════════════════════════

  public stop(): void {
    if (!this.status.isRunning) {
      return
    }

    if (this.syncIntervalId) {
      clearInterval(this.syncIntervalId)
      this.syncIntervalId = null
    }

    if (this.discrepancyIntervalId) {
      clearInterval(this.discrepancyIntervalId)
      this.discrepancyIntervalId = null
    }

    this.status.isRunning = false
    this.status.nextSyncAt = null
    this.status.nextDiscrepancyCheckAt = null

    this.notifyListeners()
  }

  // ═════════════════════════════════════════════════════════════════════════
  // SYNC INTERVAL
  // ═════════════════════════════════════════════════════════════════════════

  private startSyncInterval(): void {
    const intervalMs = this.config.syncIntervalMinutes * 60 * 1000

    this.syncIntervalId = setInterval(() => {
      this.performSync()
    }, intervalMs)

    // Calculate next sync time
    this.status.nextSyncAt = new Date(Date.now() + intervalMs)
  }

  // ═════════════════════════════════════════════════════════════════════════
  // DISCREPANCY CHECK INTERVAL
  // ═════════════════════════════════════════════════════════════════════════

  private startDiscrepancyCheckInterval(): void {
    // Check every hour if it's time to run the daily check
    const intervalMs = 60 * 60 * 1000 // 1 hour

    this.discrepancyIntervalId = setInterval(() => {
      const now = new Date()
      const currentHour = now.getHours()

      // Run if it's the scheduled hour and we haven't run today
      if (currentHour === this.config.discrepancyCheckHour) {
        const lastCheck = this.status.lastDiscrepancyCheckAt
        const today = new Date().toDateString()
        const lastCheckToday = lastCheck && lastCheck.toDateString() === today

        if (!lastCheckToday) {
          this.performDiscrepancyCheck()
        }
      }
    }, intervalMs)

    // Calculate next discrepancy check
    this.calculateNextDiscrepancyCheck()
  }

  // ═════════════════════════════════════════════════════════════════════════
  // PERFORM SYNC
  // ═════════════════════════════════════════════════════════════════════════

  private async performSync(): Promise<void> {
    if (!this.config.enabled) {
      return
    }

    try {
      const result = await mailboxTwoWaySyncService.performFullSync(
        this.sequencerType
      )

      if (result.success) {
        this.status.lastSyncAt = new Date()
        this.status.consecutiveFailures = 0

        // Process any unsent alerts
        await slackAlertService.processUnsentAlerts()
      } else {
        throw new Error(result.message)
      }

    } catch (error) {
      // Background sync failed - will retry silently
      this.status.consecutiveFailures++

      // Send alert if too many failures
      if (this.status.consecutiveFailures >= this.config.maxRetries) {
        await slackAlertService.sendSyncFailureAlert(
          this.sequencerType,
          `Background sync has failed ${this.status.consecutiveFailures} times consecutively. Last error: ${error instanceof Error ? error.message : 'Unknown error'}`
        )
      }

      // Retry with delay
      if (this.status.consecutiveFailures < this.config.maxRetries) {
        setTimeout(() => {
          this.performSync()
        }, this.config.retryDelayMinutes * 60 * 1000)
      }
    }

    // Update next sync time
    const intervalMs = this.config.syncIntervalMinutes * 60 * 1000
    this.status.nextSyncAt = new Date(Date.now() + intervalMs)
    
    this.notifyListeners()
  }

  // ═════════════════════════════════════════════════════════════════════════
  // PERFORM DISCREPANCY CHECK
  // ═════════════════════════════════════════════════════════════════════════

  private async performDiscrepancyCheck(): Promise<void> {
    // Running daily discrepancy check

    try {
      // This is handled within the full sync
      const result = await mailboxTwoWaySyncService.performFullSync(
        this.sequencerType
      )

      // Discrepancy check completed

      this.status.lastDiscrepancyCheckAt = new Date()
      this.calculateNextDiscrepancyCheck()

    } catch (error) {
      // Discrepancy check failed silently
    }

    this.notifyListeners()
  }

  // ═════════════════════════════════════════════════════════════════════════
  // CALCULATE NEXT DISCREPANCY CHECK
  // ═════════════════════════════════════════════════════════════════════════

  private calculateNextDiscrepancyCheck(): void {
    const now = new Date()
    const next = new Date(now)
    next.setHours(this.config.discrepancyCheckHour, 0, 0, 0)

    // If today's time has passed, schedule for tomorrow
    if (next <= now) {
      next.setDate(next.getDate() + 1)
    }

    this.status.nextDiscrepancyCheckAt = next
  }

  // ═════════════════════════════════════════════════════════════════════════
  // MANUAL SYNC TRIGGER
  // ═════════════════════════════════════════════════════════════════════════

  public async triggerManualSync(): Promise<void> {
    // Manual sync triggered
    await this.performSync()
  }

  // ═════════════════════════════════════════════════════════════════════════
  // CONFIGURATION
  // ═════════════════════════════════════════════════════════════════════════

  public updateConfig(config: Partial<SchedulerConfig>): void {
    this.config = { ...this.config, ...config }
    // Scheduler config updated

    // Restart if running to apply new config
    if (this.status.isRunning) {
      this.stop()
      this.start(this.sequencerType)
    }
  }

  public getConfig(): SchedulerConfig {
    return { ...this.config }
  }

  // ═════════════════════════════════════════════════════════════════════════
  // STATUS
  // ═════════════════════════════════════════════════════════════════════════

  public getStatus(): SchedulerStatus {
    return { ...this.status }
  }

  public isRunning(): boolean {
    return this.status.isRunning
  }

  // ═════════════════════════════════════════════════════════════════════════
  // LISTENERS
  // ═════════════════════════════════════════════════════════════════════════

  public addListener(callback: (status: SchedulerStatus) => void): () => void {
    this.listeners.push(callback)
    
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback)
    }
  }

  private notifyListeners(): void {
    const status = this.getStatus()
    this.listeners.forEach(callback => {
      try {
        callback(status)
      } catch (error) {
        console.error('Error in scheduler listener:', error)
      }
    })
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// EXPORTED SINGLETON
// ═══════════════════════════════════════════════════════════════════════════

export const BackgroundSyncScheduler = new BackgroundSyncSchedulerClass()

// ═══════════════════════════════════════════════════════════════════════════
// REACT HOOK FOR SCHEDULER STATUS
// ═══════════════════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react'

export function useBackgroundSyncScheduler() {
  const [status, setStatus] = useState<SchedulerStatus>(BackgroundSyncScheduler.getStatus())

  useEffect(() => {
    // Subscribe to status updates
    const unsubscribe = BackgroundSyncScheduler.addListener(setStatus)
    
    // Get initial status
    setStatus(BackgroundSyncScheduler.getStatus())
    
    return unsubscribe
  }, [])

  return {
    status,
    isRunning: status.isRunning,
    start: (type: SequencerType) => BackgroundSyncScheduler.start(type),
    stop: () => BackgroundSyncScheduler.stop(),
    triggerManualSync: () => BackgroundSyncScheduler.triggerManualSync(),
    updateConfig: (config: Partial<SchedulerConfig>) => BackgroundSyncScheduler.updateConfig(config),
    getConfig: () => BackgroundSyncScheduler.getConfig(),
  }
}






